import SpaceMeasurementContainer from '@/components/filters/SpaceMeasurementContainer';
import VenueTypeContainer from '@/components/filters/VenueTypeContainer';
import BrandAndChainFilterContainer from '@/components/filters/BrandAndChainFilterContainer';
import type { Locale } from '@/i18n';
import type { Filter, MeasurementUnit } from '@/models/Filter';
import type { FilterOptionsAndLabels } from '@/utils/filters/options/getFilterOptions';
import AccountSettingsContainer from '@/components/filters/AccountSettingsContainer';
import SuggestedFeatureContainer from '@/components/filters/SuggestedFeatureContainer';
import type { Dispatch, SetStateAction } from 'react';
import AllFilters from '@/components/filters/AllFilters';
import { useState } from 'react';
import type { FeatureConfig } from '@/fetchers/getFeatureConfigs';

type Props = {
  searchTerm: string;
  measurementUnit: MeasurementUnit;
  geo: string | undefined;
  appliedFilters: Filter;
  applicableFilters: Array<string>;
  options: FilterOptionsAndLabels;
  locale: Locale;
  isFiltersPanelOpen: boolean;
  setIsFiltersPanelOpen: (isOpen: boolean) => void;
  locationRadiusMessage: string;
  isLoggedIn: boolean;
  sortOptions: { label: string; value: string }[];
  sortType: string;
  setSortingType: Dispatch<SetStateAction<string>>;
  setSortingLabel: Dispatch<SetStateAction<string>>;
  setSort: Dispatch<SetStateAction<string>>;
  sort: string;
  featureConfig: FeatureConfig | null;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  aiMode: boolean;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

export default function FilterContainer({
  aiMode,
  searchTerm,
  measurementUnit,
  geo,
  appliedFilters,
  applicableFilters,
  options,
  locale,
  isFiltersPanelOpen,
  setIsFiltersPanelOpen,
  locationRadiusMessage,
  isLoggedIn,
  sortOptions,
  sortType,
  setSortingType,
  setSortingLabel,
  sort,
  setSort,
  featureConfig,
  setIsRfpBannerVisible,
  isSearchExpanded,
  setIsSearchExpanded
}: Props) {
  const {
    venueTypeOptions,
    spaceMeasurementOptions,
    accountTagsOptions,
    rateHistoryLabels,
    localizedLabels,
    suggestedFeatureOptions,
    roomLayoutOptions,
    errorLabels
  } = options;
  const {
    venueType,
    howMuchSpace,
    calculateCapacity,
    maxMinError,
    wholeNumberRequired,
    selectAll,
    allFilters,
    chainBrandSurfaceTitle,
    resetAllFilters,
    cancelAllFilters,
    applyAllFilters,
    apply,
    clear,
    accountSettingsTitle,
    accountTagsTitle,
    cancelledSpaceTitle,
    lessTag,
    moreTag,
    cancelledSpaceLabel,
    cancelledSpaceFlyout,
    suggestedFeatureTitle,
    close,
    back
  } = localizedLabels;

  const [showShadow, setShowShadow] = useState(false);

  const filterLabels = {
    allFilters,
    resetAllFilters,
    cancelAllFilters,
    applyAllFilters,
    apply,
    clear
  };

  return (
    <div className={`${isSearchExpanded ? 'gap-4' : 'gap-2'} lg:flex`}>
      <AllFilters
        aiMode={aiMode}
        searchTerm={searchTerm}
        geo={geo}
        locale={locale}
        measurementUnit={measurementUnit}
        options={options}
        appliedFilters={appliedFilters}
        applicableFilters={applicableFilters}
        setIsFiltersPanelOpen={setIsFiltersPanelOpen}
        isFiltersPanelOpen={isFiltersPanelOpen}
        locationRadiusMessage={locationRadiusMessage}
        isLoggedIn={isLoggedIn}
        sortOptions={sortOptions}
        setSortingType={setSortingType}
        setSortingLabel={setSortingLabel}
        sortType={sortType}
        sort={sort}
        setSort={setSort}
        setShowShadow={setShowShadow}
        featureConfig={featureConfig}
        setIsRfpBannerVisible={setIsRfpBannerVisible}
        isSearchExpanded={isSearchExpanded}
        setIsSearchExpanded={setIsSearchExpanded}
      />

      {!aiMode ? (
        <div className={`hidden ${isSearchExpanded ? 'gap-4' : 'gap-2'} lg:flex`}>
          <SuggestedFeatureContainer
            searchTerm={searchTerm}
            options={suggestedFeatureOptions}
            locale={locale}
            appliedFilters={appliedFilters}
            labels={{ ...filterLabels, title: suggestedFeatureTitle }}
            setSortingLabel={setSortingLabel}
            setSortingType={setSortingType}
            sortOptions={sortOptions}
            sortType={sortType}
            sort={sort}
            setSort={setSort}
            setIsRfpBannerVisible={setIsRfpBannerVisible}
            testID="sf-suggested-feature"
            isSearchExpanded={isSearchExpanded}
            setIsSearchExpanded={setIsSearchExpanded}
          />
          <div className={`${isLoggedIn ? 'hidden xl:flex' : ''}`}>
            <VenueTypeContainer
              setIsSearchExpanded={setIsSearchExpanded}
              isSearchExpanded={isSearchExpanded}
              searchTerm={searchTerm}
              options={venueTypeOptions}
              locale={locale}
              appliedFilters={appliedFilters}
              labels={{
                ...filterLabels,
                title: venueType,
                selectAll
              }}
              setSortingLabel={setSortingLabel}
              setSortingType={setSortingType}
              sortOptions={sortOptions}
              sortType={sortType}
              sort={sort}
              setSort={setSort}
              showShadow={showShadow}
              setShowShadow={setShowShadow}
              setIsRfpBannerVisible={setIsRfpBannerVisible}
              testID="sf-venue-type"
            />
          </div>
          <div className={`hidden ${isLoggedIn ? '2xl:flex' : 'xl:flex'}`}>
            <BrandAndChainFilterContainer
              setIsSearchExpanded={setIsSearchExpanded}
              isSearchExpanded={isSearchExpanded}
              searchTerm={searchTerm}
              geo={geo}
              locale={locale}
              measurementUnit={measurementUnit}
              appliedFilters={appliedFilters}
              labels={{
                ...filterLabels,
                title: chainBrandSurfaceTitle
              }}
              setSortingLabel={setSortingLabel}
              setSortingType={setSortingType}
              sortOptions={sortOptions}
              sortType={sortType}
              sort={sort}
              setSort={setSort}
              setIsRfpBannerVisible={setIsRfpBannerVisible}
              testID="sf-brand-chains"
            />
          </div>
          <div className={`hidden ${isLoggedIn ? '' : '2xl:flex'}`}>
            <SpaceMeasurementContainer
              setIsSearchExpanded={setIsSearchExpanded}
              isSearchExpanded={isSearchExpanded}
              searchTerm={searchTerm}
              content={spaceMeasurementOptions}
              locale={locale}
              appliedFilters={appliedFilters}
              applicableFilters={applicableFilters}
              labels={{
                ...filterLabels,
                maxMinError,
                wholeNumberRequired,
                howMuchSpace,
                calculateCapacity,
                close,
                back
              }}
              setSortingLabel={setSortingLabel}
              setSortingType={setSortingType}
              sortOptions={sortOptions}
              sortType={sortType}
              sort={sort}
              setSort={setSort}
              roomLayoutOptions={roomLayoutOptions}
              measurementUnit={measurementUnit}
              errorLabels={errorLabels}
              setIsRfpBannerVisible={setIsRfpBannerVisible}
              testID="sf-space-measurement"
            />
          </div>
          <AccountSettingsContainer
            setIsSearchExpanded={setIsSearchExpanded}
            isSearchExpanded={isSearchExpanded}
            searchTerm={searchTerm}
            options={accountTagsOptions}
            appliedFilters={appliedFilters}
            applicableFilters={applicableFilters}
            labels={{
              ...filterLabels,
              accountSettingsTitle,
              accountTagsTitle,
              cancelledSpaceTitle,
              lessTag,
              moreTag,
              cancelledSpaceLabel,
              cancelledSpaceFlyout,
              rateHistoryLabels
            }}
            locale={locale}
            isLoggedIn={isLoggedIn}
            setSortingLabel={setSortingLabel}
            setSortingType={setSortingType}
            sortOptions={sortOptions}
            sortType={sortType}
            sort={sort}
            setSort={setSort}
            featureConfig={featureConfig}
            setIsRfpBannerVisible={setIsRfpBannerVisible}
            testID="sf-account-settings"
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
